import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Navigation from './Navigation';
import Footer from './Footer';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import apiClient from '../utils/apiClient';

const URLS_PER_PAGE = 10;

const Dashboard = () => {
  const [urls, setUrls] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [newUrl, setNewUrl] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editedUrl, setEditedUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [subscription, setSubscription] = useState(null);
  const [urlLimitInfo, setUrlLimitInfo] = useState(null);
  const [deletingUrl, setDeletingUrl] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [showInvoices, setShowInvoices] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Function to check if a URL is duplicate
  const isDuplicateUrl = (urlToCheck, currentId) => {
    return urls.some(url =>
      url.originalUrl === urlToCheck && url.shortId !== currentId
    );
  };

  // Auto-hide messages after 5 seconds
  useEffect(() => {
    document.title = 'Dashboard - ä.dk URL Shortener';
    if (error) {
      // Start collapse after 5 seconds
      const collapseTimer = setTimeout(() => {
        setError('');
      }, 5000);

      return () => {
        clearTimeout(collapseTimer);
      };
    }
  }, [error]);

  // Function to show a message temporarily
  const showMessage = (message, isError = false) => {
    if (isError) {
      setError(message);
      setTimeout(() => setError(''), 3000);
    } else {
      setSuccess(message);
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  // Fetch invoices function
  const fetchInvoices = async () => {
    setInvoicesLoading(true);
    try {
      const data = await apiClient.getInvoices(user.token);
      setInvoices(data.invoices);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setError('Failed to load invoices');
    } finally {
      setInvoicesLoading(false);
    }
  };

  // Toggle invoices visibility
  const toggleInvoices = () => {
    if (!showInvoices && invoices.length === 0) {
      fetchInvoices();
    }
    setShowInvoices(!showInvoices);
  };

  // Fetch subscription status
  const fetchSubscriptionStatus = async () => {
    try {
      console.log('Fetching subscription status...');
      const data = await apiClient.getSubscriptionStatus(user.token);
      console.log('Received subscription data:', data);
      setSubscription({
        isSubscribed: data.isSubscribed,
        status: data.subscription?.status || 'inactive',
        ...data.subscription
      });
      return data;
    } catch (error) {
      console.error('Error fetching subscription status:', error);
      showMessage(error.message || 'Failed to fetch subscription status', true);
      return null;
    }
  };

  // Fetch subscription status on mount and when user changes
  useEffect(() => {
    if (user?.token) {
      console.log('User token present, fetching subscription status');
      fetchSubscriptionStatus();
    }
  }, [user]);

  useEffect(() => {
    // Check for session_id in URL
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (sessionId && user?.token) {
      console.log('Found session ID in URL, checking subscription status');
      apiClient.verifySubscriptionSuccess(sessionId, user.token)
      .then(data => {
        console.log('Subscription success response:', data);
        if (data.isSubscribed) {
          setSubscription(data.subscription);
        }
        // Remove session_id from URL
        window.history.replaceState({}, document.title, "/dashboard");
      })
      .catch(error => {
        console.error('Error checking subscription success:', error);
      });
    }
  }, [user]);

  // Separate function to fetch URLs
  const fetchData = useCallback(async () => {
    if (!user?.token) {
      setError('Please log in to view your URLs');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await apiClient.getDashboard(user.token);
      // Sort URLs by date (newest first) before setting state
      const sortedUrls = data.urls.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setUrls(sortedUrls);
      setUrlLimitInfo(data.limitInfo);
      setSubscription(data.subscriptionStatus);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError(error.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user, fetchData]);

  const handleDownload = (shortId) => {
    const svg = document.querySelector(`[data-shortid="${shortId}"]`);
    if (svg) {
      const svgData = new XMLSerializer().serializeToString(svg);
      const blob = new Blob([svgData], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qr-${shortId}.svg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const handleNewUrl = async (e) => {
    e.preventDefault();
    if (!newUrl) {
      showMessage('Please enter a URL', true);
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.shortenUrl(newUrl, user.token);
      console.log('Created new URL:', response);
      
      // Add the new URL to the list
      setUrls(prevUrls => [response.url, ...prevUrls]);
      
      // Update URL limit info
      if (response.limitInfo) {
        setUrlLimitInfo(response.limitInfo);
      }
      
      // Clear the input and show success
      setNewUrl('');
      showMessage('URL shortened successfully! 🎉');
    } catch (error) {
      console.error('Error creating short URL:', error);
      if (error.message?.includes('limit reached')) {
        showMessage('You\'ve reached your URL limit! Subscribe to create more URLs 🚀', true);
      } else {
        showMessage(error.message || 'Failed to create short URL', true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e, shortId) => {
    e.preventDefault();
    try {
      setLoading(true);
      const data = await apiClient.updateUrl(shortId, editedUrl, user.token);
      setUrls(prevUrls => prevUrls.map(url =>
        url.shortId === shortId ? { ...url, originalUrl: editedUrl } : url
      ));
      setEditingId(null);
      setEditedUrl('');
      setError('');
    } catch (error) {
      console.error('Error updating URL:', error);
      setError(error.message || 'Failed to update URL');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!showCancelConfirm) {
      setShowCancelConfirm(true);
      return;
    }

    setCancelLoading(true);
    try {
      console.log('Cancelling subscription...');
      const result = await apiClient.cancelSubscription(user.token);
      
      // Update subscription status from the response
      setSubscription(result.subscription);
      setShowCancelConfirm(false);
      showMessage('Subscription cancelled successfully');
    } catch (error) {
      console.error('Cancel subscription error:', error);
      showMessage(error.message || 'Failed to cancel subscription', true);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleResubscribe = async () => {
    try {
      setLoading(true);
      const response = await apiClient.resubscribe(user.token);
      window.location.href = response.sessionUrl;
    } catch (error) {
      console.error('Resubscribe error:', error);
      setError('Failed to start resubscription process');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (shortId) => {
    setDeleteLoading(true);

    try {
      await apiClient.deleteUrl(shortId, user.token);
      // Update URL limit info from response
      const data = await apiClient.getUserUrls(user.token);
      setUrlLimitInfo(data.limitInfo);

      // Update URLs list
      setUrls(urls.filter(url => url.shortId !== shortId));
      setError('');
    } catch (error) {
      console.error('Error:', error);
      setError('Failed to delete URL');
    } finally {
      setDeleteLoading(false);
      setDeletingUrl(null); // Close modal after deletion
    }
  };

  const renderDeleteModal = () => {
    if (!deletingUrl) return null;

    const url = urls.find(u => u.shortId === deletingUrl);
    if (!url) return null;

    return (
      <div className="modal-overlay">
        <div className="modal">
          <h3>Delete URL</h3>
          <p>Are you sure you want to delete this shortened URL?</p>
          <div className="url-details">
            <div className="url-detail">
              <label>Target URL:</label>
              <div className="url-value">{url.originalUrl}</div>
            </div>
            <div className="url-detail">
              <label>Short URL:</label>
              <div className="url-value">https://ä.dk/{url.shortId}</div>
            </div>
          </div>
          <div className="modal-actions">
            <button
              className="button-secondary"
              onClick={() => setDeletingUrl(null)}
              disabled={deleteLoading}
            >
              Cancel
            </button>
            <button
              className="button-danger"
              onClick={() => handleDelete(deletingUrl)}
              disabled={deleteLoading}
            >
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Add URL limit warning component
  const renderUrlLimitWarning = () => {
    if (!urlLimitInfo?.hasReachedLimit) return null;

    return (
      <div className="url-limit-warning">
        <p>
          <strong>URL Limit Reached!</strong> You have used all {urlLimitInfo.maxUrls} available URL slots.
          Please delete some URLs to create new ones.
        </p>
      </div>
    );
  };

  // New sorting function
  const sortUrls = (urlsToSort) => {
    return [...urlsToSort].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'alphabetical':
          comparison = a.originalUrl.localeCompare(b.originalUrl);
          break;
        case 'date':
          comparison = new Date(b.createdAt) - new Date(a.createdAt);
          break;
        case 'visits':
          comparison = b.visits - a.visits;
          break;
        default:
          comparison = 0;
      }

      return sortOrder === 'asc' ? -comparison : comparison;
    });
  };

  // Modify the URLs display to use sorted URLs
  const sortedUrls = sortUrls(urls);
  const totalPages = Math.ceil(sortedUrls.length / URLS_PER_PAGE);
  const currentUrls = sortedUrls.slice(
    (currentPage - 1) * URLS_PER_PAGE,
    currentPage * URLS_PER_PAGE
  );

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Check for reactivation success
  useEffect(() => {
    let pollInterval;
    const params = new URLSearchParams(window.location.search);

    if (params.get('subscription') === 'reactivated') {
      console.log('Subscription reactivated, starting polling');
      setError('');
      // Clear the URL parameter
      window.history.replaceState({}, '', '/dashboard');

      // Set up polling to check subscription status
      let attempts = 0;
      pollInterval = setInterval(async () => {
        console.log('Polling subscription status... (attempt', attempts + 1, ')');
        const updated = await fetchSubscriptionStatus();
        console.log('Polled subscription status:', updated);
        
        if (updated?.isSubscribed || attempts >= 10) {
          console.log('Stopping polling:', updated?.isSubscribed ? 'subscription active' : 'max attempts reached');
          clearInterval(pollInterval);
          
          if (!updated?.isSubscribed && attempts >= 10) {
            console.log('Subscription status not updated after max attempts');
            setError('Subscription status not updated. Please refresh the page.');
          }
        }
        attempts++;
      }, 2000); // Poll every 2 seconds
    }

    // Clean up interval
    return () => {
      if (pollInterval) {
        console.log('Cleaning up subscription polling');
        clearInterval(pollInterval);
      }
    };
  }, [fetchSubscriptionStatus]);

  if (loading) return (
    <div className="loading">
      <svg fill="#fff" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 160 160">
        <g transform="rotate(75.7 118.4 92.9) scale(0.9)">
          <circle cx="109.4" cy="76.5" r="21.4" />
          <circle cx="157" cy="95" r="17" />
          <circle cx="175.5" cy="142.5" r="16.6" />
          <circle cx="157" cy="190.1" r="14" />
          <circle cx="109.4" cy="208.6" r="9.5" />
          <circle cx="61.8" cy="190.1" r="8.4" />
          <circle cx="43.4" cy="142.5" r="8" />
          <circle cx="61.8" cy="95" r="6.9" />
        </g>
      </svg>
    </div>
  );

  return (
    <div className="page-container">
      <Navigation />
      <div className="container">
        {/* Dashboard Header with URL Count */}
        {subscription?.currentPeriodEnd && !subscription?.isSubscribed && (
          <p>
            Current period ends:{' '}
            {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
          </p>
        )}
        <div className="dashboard-header">
          <div className="header-main">
            <div className="left-section">
              <h2>Your URLs</h2>
              <span>{urlLimitInfo?.current || 0} / {urlLimitInfo?.maxUrls || 50} URLs used</span>
            </div>
            <div className="right-section">
              <div className="subscription-status">
                <div className="card signup">
                  <h2>Subscription Status</h2>
                  {subscription ? (
                    <div>
                      <p>
                        Status: <span style={{ color: subscription.status === 'active' ? '#4adb4a' : '#e60606' }}>
                          {subscription.status === 'active' ? 'Active' : 'Inactive'}
                        </span>
                      </p>
                      {subscription.status === 'cancelled' && subscription.cancelledAt && (
                        <p style={{ color: '#ffffff7e', marginTop: '1rem' }}>
                          You will lose access to your data on {new Date(new Date(subscription.cancelledAt).getTime() + (30 * 24 * 60 * 60 * 1000)).toLocaleDateString()}
                        </p>
                      )}
                      {subscription.status === 'active' ? (
                        <div>
                          {showCancelConfirm ? (
                            <div>
                              <p style={{ fontWeight: 'bold' }}>Are you sure you want to cancel your subscription?</p>
                              <p style={{ color: '#ffffff' }}>Your data will be deleted 30 days after cancellation.</p>
                              <button
                                onClick={handleCancelSubscription}
                                disabled={cancelLoading}
                                className="button"
                                style={{ backgroundColor: '#bc5e1a', color: 'white', marginRight: '0.5rem' }}
                              >
                                {cancelLoading ? 'Cancelling...' : 'Yes, Cancel'}
                              </button>
                              <button
                                onClick={() => setShowCancelConfirm(false)}
                                className="button"
                                style={{ backgroundColor: '#ffffff20', color: 'white' }}
                              >
                                No, Keep
                              </button>
                            </div>
                          ) : (
                            <div>
                              <button
                                onClick={() => setShowCancelConfirm(true)}
                                className="button"
                                style={{ backgroundColor: '#bc5e1a', color: 'white', marginRight: '0.5rem' }}
                              >
                                Cancel Subscription
                              </button>
                              <button 
                                onClick={toggleInvoices} 
                                className="button"
                                style={{ backgroundColor: '#803f0f', color: 'white' }}
                                disabled={invoicesLoading}
                              >
                                {invoicesLoading ? 'Loading...' : showInvoices ? 'Hide Invoices' : 'View Invoices'}
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <button
                          onClick={handleResubscribe}
                          className="button"
                          style={{ backgroundColor: '#803f0f', color: 'white' }}
                        >
                          Resubscribe
                        </button>
                      )}
                    </div>
                  ) : (
                    <p>Loading subscription status...</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Invoices section */}
          {showInvoices && (
            <div className="invoices-container">
              {invoicesLoading ? (
                <div className="loading">Loading invoices...</div>
              ) : invoices.length === 0 ? (
                <p>No invoices found.</p>
              ) : (
                <div className="invoices-list">
                  {invoices.map(invoice => (
                    <div key={invoice.id} className="invoice-item">
                      <div className="invoice-details">
                        <span className="invoice-date">
                          {new Date(invoice.created * 1000).toLocaleDateString()}
                        </span>
                        <span className="invoice-amount">
                          ${(invoice.total / 100).toFixed(2)}
                        </span>
                      </div>
                      <div className="invoice-actions">
                        <a
                          href={invoice.hosted_invoice_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="invoice-link"
                        >
                          View Invoice
                        </a>
                        <a
                          href={invoice.invoice_pdf}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="invoice-link"
                        >
                          Download PDF
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {/* Messages */}
          {success && (
            <div className="success-message">
              {success}
            </div>
          )}
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
          {/* URL Creation Section */}
          <div className="create-url-section">
            <form onSubmit={handleNewUrl}>
              <input
                type="url"
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                placeholder="Enter URL to shorten"
                disabled={loading || urlLimitInfo?.hasReachedLimit}
              />
              <button
                type="submit"
                disabled={!newUrl || loading || urlLimitInfo?.hasReachedLimit}
              >
                {loading ? 'Creating...' : 'Generate Short URL'}
              </button>
            </form>
            {urlLimitInfo?.hasReachedLimit && !subscription?.isSubscribed && (
              <div className="limit-notice">
                You've reached the URL limit. You can delete some URLs to create new ones, or <a href="mailto:mail@aumlaut.dk">contact us</a> for more URLs! 🚀
              </div>
            )}
          </div>

          {/* Sorting controls */}
          <div className="sorting-controls">
            <div className="sort-group">
              <label>Sort by:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="sort-select"
              >
                <option value="date">Date Created</option>
                <option value="alphabetical">URL (A-Z)</option>
                <option value="visits">Visit Count</option>
              </select>
            </div>
            <button
              onClick={() => setSortOrder(order => order === 'asc' ? 'desc' : 'asc')}
              className="sort-direction-button"
            >
              {sortOrder === 'asc' ? '▲' : '▼'}
            </button>
          </div>

          {/* URLs List with Pagination */}
          <div className="urls-container">
            {urls.length === 0 ? (
              <div className="no-urls">
                <p>You haven't created any short URLs yet.</p>
              </div>
            ) : loading ? (
              <div className="loading">Loading...</div>
            ) : (
              <>
                <div className="urls-list">
                  {currentUrls.map(url => (
                    <div key={url.shortId} className="url-item">
                      <div className="url-row">
                        <span className="url-label">Short URL:</span>
                        <div className="url-content">
                          <a href={`https://ä.dk/${url.shortId}`} target="_blank" rel="noreferrer">
                            {`https://ä.dk/${url.shortId}`}
                          </a>
                        </div>
                      </div>

                      <div className="url-row">
                        <span className="url-label">Target URL:</span>
                        <div className="url-content">
                          {editingId === url.shortId ? (
                            <form onSubmit={(e) => handleSave(e, url.shortId)} className="edit-form">
                              <input
                                type="url"
                                value={editedUrl}
                                onChange={(e) => setEditedUrl(e.target.value)}
                                className="form-input"
                                required
                              />
                              <button type="submit" className="button button-primary">Save</button>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingId(null);
                                  setEditedUrl('');
                                }}
                                className="button button-danger"
                              >
                                Cancel
                              </button>
                            </form>
                          ) : (
                            <>
                              <span className="url-content">{url.originalUrl}</span>
                              {subscription?.isSubscribed && (
                                <button
                                  onClick={() => {
                                    setEditingId(url.shortId);
                                    setEditedUrl(url.originalUrl);
                                  }}
                                  className="button button-secondary"
                                >
                                  Edit
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      <div className="url-row">
                        <span className="url-label">Visits:</span>
                        <div className="url-content">{url.visits}</div>
                      </div>
                      <div className="url-row">
                        <span className="url-label">Created:</span>
                        <div className="url-content">
                          {new Date(url.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="url-row">
                        <span className="url-label">QR Code:</span>
                        <div className="url-content qr-code-container" onClick={() => handleDownload(url.shortId)}>
                          <QRCodeSVG
                            value={`https://ä.dk/${url.shortId}`}
                            size={768}
                            level="H"
                            className="qr-code-image"
                            data-shortid={url.shortId}
                            title="Click to download QR code"
                          /><div style={{ minWidth: '100px' }}>Download SVG</div>
                        </div>
                      </div>
                      <button
                        className="delete-button"
                        onClick={() => setDeletingUrl(url.shortId)}
                        disabled={deleteLoading}
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                </div>

                {/* Pagination Controls */}
                <div className="pagination">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="pagination-button"
                  >
                    Previous
                  </button>
                  <span className="page-info">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage >= totalPages}
                    className="pagination-button"
                  >
                    Next
                  </button>
                </div>
              </>
            )}
          </div>

        </div>
        <Footer />
        {renderDeleteModal()}
      </div>
    </div>
  );
};

export default Dashboard;